// src/components/pipeline/PipelineViewSwitch.jsx
// Thin host for the pipeline lead area: owns the Board | List | Deleted
// segmented toggle and renders the chosen view, passing every prop straight
// through. Used by BOTH the Buyer and Listing pipelines.
// Spec: docs/superpowers/specs/2026-07-28-pipeline-board-list-redesign.md
//       docs/superpowers/specs/2026-08-11-deleted-records-visibility-design.md
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { LayoutGrid, List, Trash2 } from "lucide-react";
import PipelineBoardView from "./PipelineBoardView";
import PipelineListView from "./PipelineListView";
import PipelineDeletedView from "./PipelineDeletedView";

const STORAGE_KEY = "pipelineViewMode";

// "deleted" is deliberately NOT a value this can return. The stored view is
// what the pipeline reopens on, and nobody should come back tomorrow and land
// in the bin.
const readStoredView = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "list" ? "list" : "board";
  } catch {
    return "board";
  }
};

export default function PipelineViewSwitch(props) {
  const { deletedRows = [], deletedCount = 0, canManage, onRestoreDeleted, onPurgeDeleted } = props;
  const location = useLocation();

  const wantsDeleted = (search) =>
    new URLSearchParams(search).get("view") === "deleted";

  // Search sends deleted leads here with ?view=deleted. Honour it for this
  // visit only — it is never written to storage.
  const [view, setView] = useState(() =>
    wantsDeleted(location.search) ? "deleted" : readStoredView(),
  );

  // ...and honour it again if it ARRIVES while we are already mounted. Someone
  // sitting on the Buyers pipeline who searches a deleted buyer lead navigates
  // to this same route: React Router swaps the query string without remounting,
  // so reading the URL only in the initializer above would leave the click doing
  // nothing at all. (The pipelines' `?lead=` deep link had exactly this bug
  // earlier today.)
  //
  // Keyed on the search string having CHANGED, so manually picking Board or
  // List does not get snapped back to Deleted by the stale ?view= still in the
  // address bar.
  const appliedSearch = useRef(location.search);
  useEffect(() => {
    if (location.search === appliedSearch.current) return;
    appliedSearch.current = location.search;
    if (wantsDeleted(location.search)) setView("deleted");
  }, [location.search]);

  const pick = (next) => {
    setView(next);
    if (next === "deleted") return; // see readStoredView
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private browsing / storage full — the toggle still works for the session.
    }
  };

  const Segment = ({ value, label, Icon, badge }) => {
    const on = view === value;
    return (
      <button
        type="button"
        onClick={() => pick(value)}
        aria-pressed={on}
        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold transition ${
          on
            ? "bg-white text-slate-800 shadow-sm"
            : "text-slate-500 hover:text-slate-700"
        }`}
      >
        <Icon size={13} /> {label}
        {badge !== undefined && (
          <span className="text-[11px] font-normal text-slate-400">{badge}</span>
        )}
      </button>
    );
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5">
          <Segment value="board" label="Board" Icon={LayoutGrid} />
          <Segment value="list" label="List" Icon={List} />
          <Segment value="deleted" label="Deleted" Icon={Trash2} badge={deletedCount} />
        </div>
      </div>
      {view === "deleted" ? (
        <PipelineDeletedView
          rows={deletedRows}
          canManage={canManage}
          onRestore={onRestoreDeleted}
          onPurge={onPurgeDeleted}
        />
      ) : view === "list" ? (
        <PipelineListView {...props} />
      ) : (
        <PipelineBoardView {...props} />
      )}
    </div>
  );
}
